import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import { CompareArrows, ArrowUpward, ArrowDownward, Remove } from '@mui/icons-material';
import api from '../../services/authService';

const MeetingComparison = () => {
  const [meetings, setMeetings] = useState([]);
  const [meeting1, setMeeting1] = useState('');
  const [meeting2, setMeeting2] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    try {
      const response = await api.get('/analytics/meetings');
      setMeetings(response.data);
    } catch (err) {
      console.error('Error fetching meetings:', err);
      setError('Failed to load meetings');
    }
  };

  const handleCompare = async () => {
    if (!meeting1 || !meeting2) {
      setError('Please select two meetings to compare');
      return;
    }

    if (meeting1 === meeting2) {
      setError('Please select two different meetings');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(
        `/analytics/compare?meeting1=${meeting1}&meeting2=${meeting2}`
      );
      setComparison(response.data);
    } catch (err) {
      console.error('Error comparing meetings:', err);
      setError(err.response?.data?.error || 'Failed to compare meetings');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const getDifferenceIcon = (value) => {
    if (value > 0) return <ArrowUpward color="success" fontSize="small" />;
    if (value < 0) return <ArrowDownward color="error" fontSize="small" />;
    return <Remove color="action" fontSize="small" />;
  };

  const getDifferenceColor = (value) => {
    if (value > 0) return 'success';
    if (value < 0) return 'error';
    return 'default';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Meeting Comparison
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Compare analytics between two meetings to identify improvements or issues
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Meeting Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <InputLabel>First Meeting</InputLabel>
                <Select
                  value={meeting1}
                  label="First Meeting"
                  onChange={(e) => setMeeting1(e.target.value)}
                >
                  {meetings.map((meeting) => (
                    <MenuItem key={meeting.id} value={meeting.id}>
                      {meeting.title || 'Untitled'} - {formatDate(meeting.actualEndTime)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
              <CompareArrows fontSize="large" color="primary" />
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth>
                <InputLabel>Second Meeting</InputLabel>
                <Select
                  value={meeting2}
                  label="Second Meeting"
                  onChange={(e) => setMeeting2(e.target.value)}
                >
                  {meetings.map((meeting) => (
                    <MenuItem key={meeting.id} value={meeting.id}>
                      {meeting.title || 'Untitled'} - {formatDate(meeting.actualEndTime)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleCompare}
                disabled={loading || !meeting1 || !meeting2}
              >
                {loading ? <CircularProgress size={24} /> : 'Compare Meetings'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {comparison && (
        <Grid container spacing={3}>
          {/* Meeting Info Cards */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Meeting 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{comparison.meeting1.title || 'Untitled'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(comparison.meeting1.date)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Meeting 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{comparison.meeting2.title || 'Untitled'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(comparison.meeting2.date)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Comparison Table */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Metrics Comparison
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Metric</TableCell>
                        <TableCell align="center">Meeting 1</TableCell>
                        <TableCell align="center">Meeting 2</TableCell>
                        <TableCell align="center">Difference</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Engagement Score</strong></TableCell>
                        <TableCell align="center">
                          <Chip
                            label={comparison.meeting1.analytics.engagementScore}
                            color={getScoreColor(comparison.meeting1.analytics.engagementScore)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={comparison.meeting2.analytics.engagementScore}
                            color={getScoreColor(comparison.meeting2.analytics.engagementScore)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            {getDifferenceIcon(comparison.differences.engagementScore)}
                            <Chip
                              label={`${comparison.differences.engagementScore > 0 ? '+' : ''}${comparison.differences.engagementScore.toFixed(1)}`}
                              size="small"
                              color={getDifferenceColor(comparison.differences.engagementScore)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Quality Score</strong></TableCell>
                        <TableCell align="center">
                          <Chip
                            label={comparison.meeting1.analytics.qualityScore}
                            color={getScoreColor(comparison.meeting1.analytics.qualityScore)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={comparison.meeting2.analytics.qualityScore}
                            color={getScoreColor(comparison.meeting2.analytics.qualityScore)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            {getDifferenceIcon(comparison.differences.qualityScore)}
                            <Chip
                              label={`${comparison.differences.qualityScore > 0 ? '+' : ''}${comparison.differences.qualityScore.toFixed(1)}`}
                              size="small"
                              color={getDifferenceColor(comparison.differences.qualityScore)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Participants</strong></TableCell>
                        <TableCell align="center">{comparison.meeting1.analytics.participantCount}</TableCell>
                        <TableCell align="center">{comparison.meeting2.analytics.participantCount}</TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            {getDifferenceIcon(comparison.differences.participants)}
                            <Chip
                              label={`${comparison.differences.participants > 0 ? '+' : ''}${comparison.differences.participants}`}
                              size="small"
                              color={getDifferenceColor(comparison.differences.participants)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Duration</strong></TableCell>
                        <TableCell align="center">
                          {formatDuration(comparison.meeting1.analytics.totalDurationSeconds)}
                        </TableCell>
                        <TableCell align="center">
                          {formatDuration(comparison.meeting2.analytics.totalDurationSeconds)}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            {getDifferenceIcon(comparison.differences.duration)}
                            <Chip
                              label={`${comparison.differences.duration > 0 ? '+' : ''}${formatDuration(Math.abs(comparison.differences.duration))}`}
                              size="small"
                              color={getDifferenceColor(comparison.differences.duration)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Chat Messages</strong></TableCell>
                        <TableCell align="center">{comparison.meeting1.analytics.totalChatMessages}</TableCell>
                        <TableCell align="center">{comparison.meeting2.analytics.totalChatMessages}</TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            {getDifferenceIcon(comparison.differences.chatMessages)}
                            <Chip
                              label={`${comparison.differences.chatMessages > 0 ? '+' : ''}${comparison.differences.chatMessages}`}
                              size="small"
                              color={getDifferenceColor(comparison.differences.chatMessages)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default MeetingComparison;
